/**
 * Typed wrappers around every endpoint, derived from the auto-generated
 * OpenAPI schema. Each function returns the success-response type pulled
 * from `operations[opId].responses[200|201].content["application/json"]`.
 */

import type { components, operations } from "./schema";
import { apiPath, buildQuery, request, requestMultipart, type QueryParams } from "./client";

export type { QueryParams };

// ---- Convenience aliases ----
type Schemas = components["schemas"];
export type AdminMe = Schemas["AdminMe"];
export type AdminStats = Schemas["AdminStats"];
export type PositionPublic = Schemas["PositionPublic"];
export type AdminPositionDetail = Schemas["AdminPositionDetail"];
export interface IntakeSourceMetadata {
  source_channel?: string | null;
  source_campaign?: string | null;
}

export interface MerchantRequestIntakeMetadata extends IntakeSourceMetadata {
  buyer_type?: string | null;
  timezone_requirement?: string | null;
  contract_duration?: string | null;
  urgency?: string | null;
}

export interface ApplicationIntakeMetadata extends IntakeSourceMetadata {
  submission_context?: string | null;
}

export type AdminMerchantRequestDetail =
  Schemas["AdminMerchantRequestDetail"] & MerchantRequestIntakeMetadata;
export type AdminMerchantRequestUpdate = Schemas["AdminMerchantRequestUpdate"];
export type AdminApplicationDetail =
  Schemas["AdminApplicationDetail"] & ApplicationIntakeMetadata;
export type AdminApplicationUpdate = Schemas["AdminApplicationUpdate"];
export type PublishRequestPayload = Schemas["PublishRequestPayload"];
export type DeclineReason = Schemas["DeclineReason"];
export type MerchantRequestCreate = Schemas["MerchantRequestCreate"];
export type ContactCreate = Schemas["ContactCreate"];
export type LoginRequest = Schemas["LoginRequest"];
export type Page = Schemas["Page"];

export interface CockpitMetric {
  label: string;
  value: number;
  tone: "neutral" | "warning" | "success" | "danger";
}

export interface CockpitAction {
  id: string;
  kind: "role_request" | "candidate_application" | "published_position" | "follow_up";
  priority: "high" | "medium" | "low";
  title: string;
  subtitle?: string | null;
  status: string;
  href: string;
  created_at: string;
  suggested_action: string;
}

export interface AdminCockpit {
  metrics: CockpitMetric[];
  next_actions: CockpitAction[];
  operating_notes: string[];
}

export interface RoleBrief {
  id: number;
  merchant_request_id: number;
  role_title: string;
  client_company: string;
  service_type: string;
  seniority: string;
  must_have_skills: string[];
  nice_to_have_skills: string[];
  responsibilities?: string | null;
  acceptable_geos: string[];
  excluded_geos: string[];
  timezone_overlap?: string | null;
  budget?: string | null;
  contract_type?: string | null;
  start_date?: string | null;
  client_name_share_allowed: string;
  intake_quality: string;
  missing_questions: string[];
  clarifications: RoleClarification[];
  status: string;
  created_at: string;
  updated_at: string;
}

export interface RoleClarification {
  id: number;
  role_brief_id: number;
  question: string;
  answer?: string | null;
  status: string;
  applied_to_field?: string | null;
  applied_summary?: string | null;
  blocking?: boolean | null;
  why_it_matters?: string | null;
  available_actions?: string[] | null;
  field_patch?: Array<{
    field: string;
    before?: unknown;
    after?: unknown;
  }> | null;
  dismissed_at?: string | null;
  dismissed_by_admin_id?: number | null;
  answered_by_admin_id?: number | null;
  answered_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface SearchRun {
  id: number;
  role_brief_id: number;
  source: string;
  search_query: string;
  source_filters: Record<string, unknown>;
  status: string;
  notes?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface SourcingTask {
  id: number;
  search_run_id: number;
  task_type: string;
  agent_name: string;
  status: string;
  status_reason?: string;
  status_label?: string;
  instructions?: string | null;
  handoff_prompt: string;
  result_summary?: string | null;
  error_message?: string | null;
  requested_by_admin_id?: number | null;
  sourced_profile_id?: number | null;
  candidate_evaluation_id?: number | null;
  attempt_count?: number | null;
  attempt_limit?: number | null;
  max_attempts?: number | null;
  attempts_remaining?: number | null;
  cooldown_until?: string | null;
  cooldown_reason?: string | null;
  cooldown_seconds_remaining?: number | null;
  retry_after_seconds?: number | null;
  retry_after_at?: string | null;
  latest_worker_log_url?: string | null;
  latest_worker_log_path?: string | null;
  latest_worker_log_label?: string | null;
  worker_log_url?: string | null;
  worker_log_path?: string | null;
  worker_log_label?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface SourcedProfile {
  id: number;
  search_run_id: number;
  source: string;
  profile_url: string;
  public_name?: string | null;
  headline?: string | null;
  location?: string | null;
  raw_text?: string | null;
  raw_text_path?: string | null;
  extraction_status: string;
  duplicate_candidate_id?: number | null;
  status: string;
  created_at: string;
  updated_at: string;
  latest_evaluation?: CandidateEvaluation | null;
}

export interface CandidateEvaluation {
  id: number;
  role_brief_id: number;
  sourced_profile_id: number;
  candidate_id?: number | null;
  score: number;
  recommendation: string;
  evidence: string[];
  gaps: string[];
  geo_fit?: string | null;
  seniority_fit?: string | null;
  stack_fit?: string | null;
  ai_fit?: string | null;
  evaluator_version: string;
  report_markdown?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Candidate {
  id: number;
  source_profile_id?: number | null;
  source_application_id?: number | null;
  role_brief_id?: number | null;
  cv_blob_id?: number | null;
  cv_url?: string | null;
  name: string;
  linkedin_url?: string | null;
  telegram_url?: string | null;
  email?: string | null;
  location?: string | null;
  geo?: string | null;
  current_title?: string | null;
  current_company?: string | null;
  core_stack: string[];
  expected_rate?: string | null;
  availability?: string | null;
  minimum_rate?: number | null;
  rate_type?: string | null;
  currency?: string | null;
  english_level?: string | null;
  notice_period?: string | null;
  earliest_start?: string | null;
  work_authorization?: string | null;
  safe_to_share_notes?: string | null;
  consent_scope?: string | null;
  profile_status?: string | null;
  profile_headline?: string | null;
  profile_summary?: string | null;
  profile_role_title?: string | null;
  profile_skills?: string[];
  profile_experience?: string[];
  cv_blob_history?: number[];
  normalized_profile?: {
    headline?: string | null;
    summary?: string | null;
    role_title?: string | null;
    skills: string[];
    experience: string[];
    availability_rate_snapshot: Record<string, unknown>;
    raw_source_refs: Record<string, unknown>;
    source_of_truth: Record<string, string>;
  };
  consent_status: string;
  representation_status: string;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export type CandidateConsentStatus =
  | "unknown"
  | "requested"
  | "approved"
  | "denied";

export type CandidateRepresentationStatus =
  | "not_contacted"
  | "shortlist_ready"
  | "outreach_pending"
  | "contacted"
  | "replied"
  | "screening"
  | "submitted"
  | "rejected"
  | "hold"
  | "represented"
  | "placed"
  | "archived";

export interface CandidateUpdateInput {
  name?: string | null;
  linkedin_url?: string | null;
  telegram_url?: string | null;
  email?: string | null;
  location?: string | null;
  geo?: string | null;
  current_title?: string | null;
  current_company?: string | null;
  core_stack?: string[] | null;
  expected_rate?: string | null;
  availability?: string | null;
  minimum_rate?: number | null;
  rate_type?: string | null;
  currency?: string | null;
  english_level?: string | null;
  notice_period?: string | null;
  earliest_start?: string | null;
  work_authorization?: string | null;
  safe_to_share_notes?: string | null;
  consent_scope?: string | null;
  profile_status?: string | null;
  profile_headline?: string | null;
  profile_summary?: string | null;
  profile_role_title?: string | null;
  profile_skills?: string[] | null;
  profile_experience?: string[] | null;
  cv_url?: string | null;
  consent_status?: CandidateConsentStatus | null;
  representation_status?: CandidateRepresentationStatus | null;
  notes?: string | null;
}

export interface ApplicationPromoteInput {
  role_brief_id?: number | null;
  existing_candidate_id?: number | null;
  name?: string | null;
  linkedin_url?: string | null;
  telegram_url?: string | null;
  email?: string | null;
  location?: string | null;
  geo?: string | null;
  current_title?: string | null;
  current_company?: string | null;
  core_stack?: string[] | null;
  expected_rate?: string | null;
  availability?: string | null;
  cv_url?: string | null;
  consent_status?: CandidateConsentStatus | null;
  representation_status?: CandidateRepresentationStatus | null;
  notes?: string | null;
}

export interface ApplicationPromotionCandidateMatch {
  candidate_id: number;
  name: string;
  reason: string;
  email?: string | null;
  linkedin_url?: string | null;
  telegram_url?: string | null;
  current_title?: string | null;
  representation_status: string;
  source_application_id?: number | null;
  role_brief_id?: number | null;
}

export interface ApplicationPromotionCheck {
  application_id: number;
  linked_candidate_id?: number | null;
  exact_matches: ApplicationPromotionCandidateMatch[];
  fuzzy_matches: ApplicationPromotionCandidateMatch[];
}

export interface OutreachApproval {
  id: number;
  candidate_id: number;
  role_brief_id?: number | null;
  channel: string;
  draft_text: string;
  status: string;
  risk_notes?: string | null;
  approved_at?: string | null;
  sent_at?: string | null;
  created_at: string;
  updated_at: string;
}

// `Page<T>` — the generated type has `items: unknown[]`, so we narrow it here.
export type PageOf<T> = Omit<Page, "items"> & { items: T[] };

type Op<K extends keyof operations> = operations[K];
type Json200<K extends keyof operations> = Op<K> extends {
  responses: { 200: { content: { "application/json": infer R } } };
}
  ? R
  : never;
type Json201<K extends keyof operations> = Op<K> extends {
  responses: { 201: { content: { "application/json": infer R } } };
}
  ? R
  : never;

/** Summary types aren't on `components.schemas` because Pydantic's `Page`
 *  envelope is non-generic — OpenAPI emits `items: unknown[]`. Derive them
 *  by Picking from the corresponding Detail types instead. The field lists
 *  must match `AdminMerchantRequestSummary` / `AdminApplicationSummary` /
 *  `AdminPositionSummary` in [backend/app/schemas.py]. */
export type AdminMerchantRequestSummary = Pick<
  AdminMerchantRequestDetail,
  | "id"
  | "service_type"
  | "contact_name"
  | "contact_email"
  | "contact_company"
  | "position_title"
  | "seniority"
  | "engagement"
  | "location"
  | "budget"
  | "skills"
  | "status"
  | "published_position_id"
  | "source_channel"
  | "created_at"
>;

export type AdminApplicationSummary = Pick<
  AdminApplicationDetail,
  | "id"
  | "full_name"
  | "email"
  | "location"
  | "position_id"
  | "expected_rate"
  | "currency"
  | "years_experience"
  | "cv_blob_id"
  | "status"
  | "source_channel"
  | "linked_candidate_id"
  | "created_at"
>;

export type AdminPositionSummary = Pick<
  AdminPositionDetail,
  | "id"
  | "title"
  | "company_name"
  | "seniority"
  | "engagement_type"
  | "schedule"
  | "location"
  | "salary_min"
  | "salary_max"
  | "currency"
  | "skills"
  | "status"
  | "posted_at"
  | "application_count"
>;

// ===================== Admin auth =====================

export function adminLogin(body: LoginRequest) {
  return request<Json200<"adminLogin">>("POST", "/api/admin/login", body);
}

export function adminLogout() {
  return request<null>("POST", "/api/admin/logout");
}

export function adminMe() {
  return request<Json200<"adminMe">>("GET", "/api/admin/me");
}

// ===================== Admin stats =====================

export function adminStats() {
  return request<Json200<"adminStats">>("GET", "/api/admin/stats");
}

export function adminCockpit() {
  return request<AdminCockpit>("GET", "/api/admin/cockpit");
}

// ===================== Admin: role rail =====================

export function runRoleIntake(requestId: number) {
  return request<RoleBrief>("POST", `/api/admin/requests/${requestId}/intake`);
}

export function listRoleBriefs(params?: QueryParams) {
  return request<PageOf<RoleBrief>>(
    "GET",
    `/api/admin/role-briefs${buildQuery(params)}`,
  );
}

export function getRoleBrief(id: number) {
  return request<RoleBrief>("GET", `/api/admin/role-briefs/${id}`);
}

export function answerRoleClarification(roleBriefId: number, clarificationId: number, body: { answer: string }) {
  return request<RoleBrief>(
    "POST",
    `/api/admin/role-briefs/${roleBriefId}/clarifications/${clarificationId}/answer`,
    body,
  );
}

export function dismissRoleClarification(roleBriefId: number, clarificationId: number) {
  return request<RoleBrief>(
    "POST",
    `/api/admin/role-briefs/${roleBriefId}/clarifications/${clarificationId}/dismiss`,
  );
}

export function reopenRoleClarification(roleBriefId: number, clarificationId: number) {
  return request<RoleBrief>(
    "POST",
    `/api/admin/role-briefs/${roleBriefId}/clarifications/${clarificationId}/reopen`,
  );
}

export function createSearchRun(roleBriefId: number, body: {
  source: "linkedin" | "linkedin_xray_serp" | "telegram" | "github" | "referral" | "existing_pool";
  search_query: string;
  source_filters?: Record<string, unknown>;
  notes?: string | null;
}) {
  return request<SearchRun>(
    "POST",
    `/api/admin/role-briefs/${roleBriefId}/search-runs`,
    body,
  );
}

export function listSearchRuns(roleBriefId: number) {
  return request<PageOf<SearchRun>>(
    "GET",
    `/api/admin/role-briefs/${roleBriefId}/search-runs`,
  );
}

export function listRoleCandidates(roleBriefId: number) {
  return request<PageOf<Candidate>>(
    "GET",
    `/api/admin/role-briefs/${roleBriefId}/candidates`,
  );
}

export function listCandidates(params: QueryParams & { status?: string; source?: string; q?: string }) {
  return request<PageOf<Candidate>>(
    "GET",
    `/api/admin/candidates${buildQuery(params)}`,
  );
}

export function getCandidate(id: number) {
  return request<Candidate>("GET", `/api/admin/candidates/${id}`);
}

export function updateCandidate(id: number, body: CandidateUpdateInput) {
  return request<Candidate>("PATCH", `/api/admin/candidates/${id}`, body);
}

export function uploadCandidateCv(id: number, formData: FormData) {
  return requestMultipart<Candidate>(`/api/admin/candidates/${id}/cv`, formData);
}

export function renormalizeCandidateProfile(id: number) {
  return request<Candidate>("POST", `/api/admin/candidates/${id}/renormalize`);
}

export function promoteApplicationToCandidate(id: number, body: ApplicationPromoteInput = {}) {
  return request<Candidate>("POST", `/api/admin/applications/${id}/promote`, body);
}

export function checkApplicationPromotion(id: number) {
  return request<ApplicationPromotionCheck>("GET", `/api/admin/applications/${id}/promotion-check`);
}

export function sourceOneProfile(searchRunId: number, body: {
  instructions?: string | null;
} = {}) {
  return request<SourcingTask>(
    "POST",
    `/api/admin/search-runs/${searchRunId}/source-one`,
    body,
  );
}

export function listSourcingTasks(searchRunId: number) {
  return request<PageOf<SourcingTask>>(
    "GET",
    `/api/admin/search-runs/${searchRunId}/sourcing-tasks`,
  );
}

export function cancelSourcingTask(taskId: number) {
  return request<SourcingTask>(
    "POST",
    `/api/admin/sourcing-tasks/${taskId}/cancel`,
  );
}

export function retrySourcingTask(taskId: number, body: {
  instructions?: string | null;
} = {}) {
  return request<SourcingTask>(
    "POST",
    `/api/admin/sourcing-tasks/${taskId}/retry`,
    body,
  );
}

export function createSourcedProfile(searchRunId: number, body: {
  source?: "linkedin" | "telegram" | "github" | "referral" | "existing_pool";
  profile_url: string;
  public_name?: string | null;
  headline?: string | null;
  location?: string | null;
  raw_text?: string | null;
  raw_text_path?: string | null;
  extraction_status?: string;
}) {
  return request<SourcedProfile>(
    "POST",
    `/api/admin/search-runs/${searchRunId}/sourced-profiles`,
    body,
  );
}

export function listSourcedProfiles(searchRunId: number) {
  return request<PageOf<SourcedProfile>>(
    "GET",
    `/api/admin/search-runs/${searchRunId}/sourced-profiles`,
  );
}

export function skipSourcedProfile(profileId: number) {
  return request<SourcedProfile>(
    "POST",
    `/api/admin/sourced-profiles/${profileId}/skip`,
  );
}

export function evaluateSourcedProfile(profileId: number, body: {
  score: number;
  recommendation: "contact" | "manual_review" | "hold" | "skip";
  evidence?: string[];
  gaps?: string[];
  geo_fit?: string | null;
  seniority_fit?: string | null;
  stack_fit?: string | null;
  ai_fit?: string | null;
  evaluator_version?: string;
  report_markdown?: string | null;
}) {
  return request<CandidateEvaluation>(
    "POST",
    `/api/admin/sourced-profiles/${profileId}/evaluate`,
    body,
  );
}

export function aiEvaluateSourcedProfile(profileId: number) {
  return request<CandidateEvaluation>(
    "POST",
    `/api/admin/sourced-profiles/${profileId}/ai-evaluate`,
  );
}

export function promoteSourcedProfile(profileId: number, body: {
  name?: string | null;
  linkedin_url?: string | null;
  location?: string | null;
  geo?: string | null;
  current_title?: string | null;
  core_stack?: string[];
  consent_status?: "unknown" | "requested" | "approved" | "denied";
  representation_status?: CandidateRepresentationStatus;
  notes?: string | null;
}) {
  return request<Candidate>(
    "POST",
    `/api/admin/sourced-profiles/${profileId}/promote`,
    body,
  );
}

export function createOutreachApproval(body: {
  candidate_id: number;
  role_brief_id?: number | null;
  channel: "linkedin_connect" | "linkedin_message" | "telegram" | "email" | "candidate_share";
  draft_text: string;
  risk_notes?: string | null;
}) {
  return request<OutreachApproval>("POST", "/api/admin/outreach-approvals", body);
}

// ===================== Admin: merchant requests =====================

export interface RequestListParams extends QueryParams {
  status?: "all" | "new" | "in_review" | "published" | "declined" | "archived";
  q?: string;
  page?: number;
  page_size?: number;
}

export function listMerchantRequests(params?: RequestListParams) {
  return request<PageOf<AdminMerchantRequestSummary>>(
    "GET",
    `/api/admin/requests${buildQuery(params)}`,
  );
}

export function getMerchantRequest(id: number) {
  return request<AdminMerchantRequestDetail>("GET", `/api/admin/requests/${id}`);
}

export function updateMerchantRequest(id: number, body: AdminMerchantRequestUpdate) {
  return request<AdminMerchantRequestDetail>(
    "PATCH",
    `/api/admin/requests/${id}`,
    body,
  );
}

export function publishMerchantRequest(id: number, body: PublishRequestPayload) {
  return request<AdminMerchantRequestDetail>(
    "POST",
    `/api/admin/requests/${id}/publish`,
    body,
  );
}

export function declineMerchantRequest(id: number, reason?: string | null) {
  return request<AdminMerchantRequestDetail>(
    "POST",
    `/api/admin/requests/${id}/decline`,
    { reason: reason ?? null } satisfies DeclineReason,
  );
}

export function archiveMerchantRequest(id: number) {
  return request<null>("DELETE", `/api/admin/requests/${id}`);
}

// ===================== Admin: applications =====================

export interface ApplicationListParams extends QueryParams {
  status?: "all" | "new" | "in_review" | "forwarded" | "declined" | "archived";
  position_id?: number;
  q?: string;
  page?: number;
  page_size?: number;
}

export function listApplications(params?: ApplicationListParams) {
  return request<PageOf<AdminApplicationSummary>>(
    "GET",
    `/api/admin/applications${buildQuery(params)}`,
  );
}

export function getApplication(id: number) {
  return request<AdminApplicationDetail>("GET", `/api/admin/applications/${id}`);
}

export function updateApplication(id: number, body: AdminApplicationUpdate) {
  return request<AdminApplicationDetail>(
    "PATCH",
    `/api/admin/applications/${id}`,
    body,
  );
}

export function forwardApplication(id: number) {
  return request<AdminApplicationDetail>(
    "POST",
    `/api/admin/applications/${id}/forward`,
    {},
  );
}

export function declineApplication(id: number, reason?: string | null) {
  return request<AdminApplicationDetail>(
    "POST",
    `/api/admin/applications/${id}/decline`,
    { reason: reason ?? null } satisfies DeclineReason,
  );
}

export function archiveApplication(id: number) {
  return request<null>("DELETE", `/api/admin/applications/${id}`);
}

export function archiveCandidate(id: number) {
  return request<null>("DELETE", `/api/admin/candidates/${id}`);
}

export function archiveRoleBrief(id: number) {
  return request<null>("DELETE", `/api/admin/role-briefs/${id}`);
}

export function blobUrl(id: number): string {
  return apiPath(`/api/admin/blobs/${id}`);
}

// ===================== Admin: positions =====================

export interface AdminPositionListParams extends QueryParams {
  status?: "all" | "active" | "filled" | "closed";
  q?: string;
  page?: number;
  page_size?: number;
}

export function listAdminPositions(params?: AdminPositionListParams) {
  return request<PageOf<AdminPositionSummary>>(
    "GET",
    `/api/admin/positions${buildQuery(params)}`,
  );
}

export function getAdminPosition(id: number) {
  return request<AdminPositionDetail>("GET", `/api/admin/positions/${id}`);
}

export async function findRoleBriefForPosition(positionId: number): Promise<RoleBrief | null> {
  const position = await getAdminPosition(positionId);
  if (!position.source_request_id) return null;

  let page = 1;
  while (true) {
    const result = await listRoleBriefs({ page, page_size: 100 });
    const match = result.items.find(
      (item) => item.merchant_request_id === position.source_request_id,
    );
    if (match) return match;
    if (!result.items.length || page * result.page_size >= result.total) return null;
    page += 1;
  }
}

// ===================== Public: positions =====================

export interface PublicPositionListParams extends QueryParams {
  q?: string;
  seniority?: string[];
  engagement_type?: string[];
  stack?: string[];
  min_rate?: number;
  max_rate?: number;
  posted?: "any" | "24h" | "week" | "month";
  sort?: "newest" | "salary_desc";
  page?: number;
  page_size?: number;
}

export function listPositions(params?: PublicPositionListParams) {
  return request<PageOf<PositionPublic>>(
    "GET",
    `/api/positions${buildQuery(params)}`,
  );
}

export function getPosition(id: number) {
  return request<PositionPublic>("GET", `/api/positions/${id}`);
}

// ===================== Public: submissions =====================

export function submitMerchantRequest(body: MerchantRequestCreate) {
  return request<Json201<"submitMerchantRequest">>(
    "POST",
    "/api/merchant-requests",
    body,
  );
}

export function submitContact(body: ContactCreate) {
  return request<Json201<"submitContact">>("POST", "/api/contact", body);
}

export function submitApplication(formData: FormData) {
  return requestMultipart<Json201<"submitApplication">>(
    "/api/applications",
    formData,
  );
}
